/**
 * Message Preparer
 *
 * Prepares messages for LLM calls by:
 * 1. Storing FULL messages in ThreadManager (with all metadata/reminders)
 * 2. Preparing CLEANED copies for LLM (stripped of old content, fresh context added)
 *
 * Key principle: We store MORE than we send to the LLM.
 * This allows us to track metadata and avoid re-processing.
 *
 * Analogous to Excel's `prepare_messages.py`
 */
import { REMINDER_TAG, REMINDER_OPEN_TAG, REMINDER_CLOSE_TAG, FIRST_MESSAGE_REMINDER_TAG, SYSTEM_REMINDER_TAG, SYSTEM_REMINDER_OPEN_TAG, SYSTEM_REMINDER_CLOSE_TAG, REMINDER_IMAGE_MARKER, } from './types';
import { getAgentServices } from '../../services';
// ═══════════════════════════════════════════════════════════════════════════
// MESSAGE PREPARER CLASS
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Prepares messages for LLM calls with reminder injection and stripping
 */
export class MessagePreparer {
    getReminderCollector(debugContext) {
        const collector = getAgentServices().reminderCollector;
        if (!collector) {
            console.warn(`[MessagePreparer] reminderCollector unavailable during ${debugContext}; skipping related reminders.`);
            return null;
        }
        return collector;
    }
    formatReminderWithCollector(formatterName, payload, debugContext) {
        const collector = this.getReminderCollector(debugContext);
        const formatter = collector?.[formatterName];
        if (typeof formatter !== 'function') {
            console.warn(`[MessagePreparer] reminderCollector.${formatterName} unavailable during ${debugContext}; skipping related reminders.`);
            return null;
        }
        try {
            const formatted = formatter.call(collector, payload);
            return typeof formatted === 'string' && formatted.trim().length > 0 ? formatted : null;
        }
        catch (error) {
            console.warn(`[MessagePreparer] reminderCollector.${formatterName} failed during ${debugContext}:`, error);
            return null;
        }
    }
    // ═══════════════════════════════════════════════════════════════════════
    // REMINDER INJECTION
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Wrap content in reminder tags
     */
    wrapInReminderTag(content) {
        return `${REMINDER_OPEN_TAG}\n${content}\n${REMINDER_CLOSE_TAG}`;
    }
    /**
     * Wrap content in system reminder tags
     */
    wrapInSystemReminderTag(content) {
        return `${SYSTEM_REMINDER_OPEN_TAG}\n${content}\n${SYSTEM_REMINDER_CLOSE_TAG}`;
    }
    /**
     * Raw reminders can be pre-tagged by callers for special lifecycle behavior.
     * Keep those wrappers intact instead of re-wrapping with non_user tags.
     */
    isPreTaggedReminder(content) {
        const trimmed = content.trim();
        return (new RegExp(`^<${REMINDER_TAG}(\\s+[^>]*)?>`, "i").test(trimmed) ||
            new RegExp(`^<${SYSTEM_REMINDER_TAG}(\\s+[^>]*)?>`, "i").test(trimmed) ||
            new RegExp(`^<${FIRST_MESSAGE_REMINDER_TAG}(\\s+[^>]*)?>`, "i").test(trimmed));
    }
    /**
     * Build reminder content strings as an array (one per reminder type)
     * Each reminder is wrapped in its own <non_user_automated_reminder> tags
     *
     * This is the NEW pattern that returns separate content parts for each reminder.
     */
    buildReminderStrings(context, debugLabel) {
        const reminders = [];
        const debugParts = [];
        // Current local date/time context
        if (context.currentDateTimeReminder) {
            reminders.push(this.wrapInReminderTag(context.currentDateTimeReminder));
            debugParts.push('CURRENT_DATE_TIME: present');
        }
        // Presentation context
        if (context.presentation) {
            const presentationReminder = this.formatReminderWithCollector('formatPresentationReminder', context.presentation, 'buildReminderStrings.presentation');
            if (presentationReminder) {
                reminders.push(this.wrapInReminderTag(presentationReminder));
                debugParts.push(`PRESENTATION: ${context.presentation.documentId} (${context.presentation.totalSlides} slides, current: ${context.presentation.currentSlideIndex ?? 'unknown'})`);
            }
        }
        // Deck registry context (for search agents)
        if (context.deckRegistry) {
            const deckRegistryReminder = this.formatReminderWithCollector('formatDeckRegistryReminder', context.deckRegistry, 'buildReminderStrings.deckRegistry');
            if (deckRegistryReminder) {
                reminders.push(this.wrapInReminderTag(deckRegistryReminder));
                debugParts.push(`DECK_REGISTRY: ${context.deckRegistry.totalDecks} decks`);
            }
        }
        // Presentation overview context (styles + layouts)
        if (context.presentationOverview) {
            const overviewReminder = this.formatReminderWithCollector('formatPresentationOverviewReminder', context.presentationOverview, 'buildReminderStrings.presentationOverview');
            if (overviewReminder) {
                reminders.push(this.wrapInReminderTag(overviewReminder));
                debugParts.push(`PRESENTATION_OVERVIEW: ${context.presentationOverview.totalSlides} slides, ${context.presentationOverview.topStyles.length} styles, ${context.presentationOverview.layouts.length} layouts [${context.presentationOverview.status}]`);
            }
        }
        // Custom instructions
        if (context.customInstructions) {
            reminders.push(this.wrapInReminderTag(`---- CUSTOM INSTRUCTIONS ----\n${context.customInstructions.instructions}`));
            debugParts.push('CUSTOM_INSTRUCTIONS: present');
        }
        // External context
        if (context.externalContext) {
            for (const ext of context.externalContext) {
                const extReminder = this.formatReminderWithCollector('formatExternalContextReminder', ext, 'buildReminderStrings.externalContext');
                if (extReminder) {
                    reminders.push(this.wrapInReminderTag(extReminder));
                }
            }
            debugParts.push(`EXTERNAL_CONTEXT: ${context.externalContext.length} items`);
        }
        // Security warning (always last)
        if (context.securityWarning) {
            reminders.push(this.wrapInReminderTag(context.securityWarning));
            debugParts.push('SECURITY_WARNING: present');
        }
        // Raw reminders
        if (context.rawReminders) {
            for (const raw of context.rawReminders) {
                reminders.push(this.isPreTaggedReminder(raw) ? raw : this.wrapInReminderTag(raw));
            }
            debugParts.push(`RAW_REMINDERS: ${context.rawReminders.length} items`);
        }
        // Debug logging - comprehensive and collapsible
        const label = debugLabel || 'MessagePreparer';
        console.group(`[${label}] 🔔 REMINDER SYSTEM - BUILDING CONTENT PARTS`);
        console.log('Total reminder parts:', reminders.length);
        console.log('Components:', debugParts);
        // Log deck registry details if present
        if (context.deckRegistry) {
            console.log('📚 DECK REGISTRY:', {
                totalDecks: context.deckRegistry.totalDecks,
                currentDocumentId: context.deckRegistry.currentDocumentId || 'none',
                decks: context.deckRegistry.decks.map(deck => ({
                    index: deck.index,
                    title: deck.title,
                    slidesReady: deck.slidesReady,
                    totalSlides: deck.totalSlides,
                    status: deck.status,
                    isCurrent: deck.isCurrent,
                    slideDescriptionsCount: deck.slideDescriptions?.length ?? 0
                }))
            });
        }
        // Log presentation context if present
        if (context.presentation) {
            const presentationSlides = Array.isArray(context.presentation.slides)
                ? context.presentation.slides
                : [];
            if (!Array.isArray(context.presentation.slides)) {
                console.warn('[MessagePreparer] presentation.slides missing or invalid; using empty array for debug logging');
            }
            const slidesWithDesc = presentationSlides.filter(s => s.shortDescription).length;
            console.log('📊 PRESENTATION CONTEXT:', {
                documentId: context.presentation.documentId,
                totalSlides: context.presentation.totalSlides,
                currentSlideIndex: context.presentation.currentSlideIndex ?? 'UNKNOWN',
                slidesWithDescriptions: `${slidesWithDesc}/${presentationSlides.length}`
            });
        }
        console.groupEnd();
        return reminders;
    }
    /**
     * Build reminder content string from context (LEGACY - for backward compatibility)
     * @deprecated Use buildReminderStrings instead for proper array-based content
     */
    buildReminderContent(context, debugLabel) {
        const reminders = this.buildReminderStrings(context, debugLabel);
        return reminders.join('\n\n');
    }
    /**
     * Add reminders to a user message as SEPARATE CONTENT PARTS
     *
     * This follows the Python pattern where each reminder is a separate
     * text content part in the content array, making them easy to strip later.
     *
     * @param message - The original user message content (string or array)
     * @param context - Reminder context to inject
     * @param debugLabel - Label for debug logging
     * @returns Message content as array with reminders appended as separate text parts
     */
    addRemindersToMessage(message, context, debugLabel) {
        // Start with original content as array
        const contentParts = [];
        if (typeof message === 'string') {
            contentParts.push({ type: 'text', text: message });
        }
        else if (Array.isArray(message)) {
            contentParts.push(...message);
        }
        // Build individual reminder strings (each already wrapped in tags)
        const reminderStrings = this.buildReminderStrings(context, debugLabel);
        // Append each reminder as a separate content part
        for (const reminder of reminderStrings) {
            contentParts.push({ type: 'text', text: reminder });
        }
        // Log the final content structure
        const label = debugLabel || 'MessagePreparer';
        console.group(`[${label}] ✅ CONTENT ARRAY BUILT`);
        console.log('Summary:', {
            totalParts: contentParts.length,
            userMessageParts: contentParts.length - reminderStrings.length,
            reminderParts: reminderStrings.length
        });
        console.log('Parts:', contentParts.map((part, i) => {
            if (part.type === 'text') {
                const isReminder = part.text.includes('<non_user_automated_reminder>');
                return {
                    index: i,
                    type: isReminder ? '🔔 REMINDER' : '💬 USER',
                    text: part.text // Show full text content
                };
            }
            else {
                // part.type === 'image_url'
                return {
                    index: i,
                    type: '🖼️ IMAGE',
                    imageUrl: part.image_url?.url ?
                        (part.image_url.url.length > 100 ?
                            part.image_url.url.substring(0, 100) + '... [base64 truncated]' :
                            part.image_url.url) :
                        '[no url]'
                };
            }
        }));
        console.groupEnd();
        return contentParts;
    }
    // ═══════════════════════════════════════════════════════════════════════
    // REMINDER STRIPPING
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Remove reminder tags and their content from text
     */
    stripReminders(text) {
        // Remove non_user_automated_reminder tags
        let result = this.removeTagContent(text, REMINDER_TAG);
        // Remove system_reminder tags
        result = this.removeTagContent(result, SYSTEM_REMINDER_TAG);
        // Clean up extra whitespace
        result = result.replace(/\n{3,}/g, '\n\n').trim();
        return result;
    }
    /**
     * Remove a specific tag and its content from text
     */
    removeTagContent(text, tagName) {
        const openTag = `<${tagName}>`;
        const closeTag = `</${tagName}>`;
        let result = text;
        let startIdx = result.indexOf(openTag);
        while (startIdx !== -1) {
            const endIdx = result.indexOf(closeTag, startIdx);
            if (endIdx !== -1) {
                // Remove the tag and everything between
                result = result.slice(0, startIdx) + result.slice(endIdx + closeTag.length);
            }
            else {
                // No closing tag found - remove just the opening tag
                result = result.slice(0, startIdx) + result.slice(startIdx + openTag.length);
            }
            startIdx = result.indexOf(openTag);
        }
        return result;
    }
    /**
     * Check if a message contains reminders
     */
    hasReminders(content) {
        const text = this.contentToText(content);
        return text.includes(REMINDER_OPEN_TAG) || text.includes(SYSTEM_REMINDER_OPEN_TAG);
    }
    // ═══════════════════════════════════════════════════════════════════════
    // MESSAGE PREPARATION
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Prepare messages for LLM call
     *
     * This is the main method that:
     * 1. Strips reminders from all messages EXCEPT the last one
     * 2. Optionally adds fresh reminders to the last user message
     *
     * @param messages - Full message history (with reminders)
     * @param freshContext - Optional fresh context to add to the last message
     * @returns Cleaned messages ready for LLM
     */
    prepareMessagesForLlm(messages, freshContext) {
        if (messages.length === 0) {
            return [];
        }
        // Find the last user message with reminders
        const lastMessageWithReminders = this.findLastMessageWithReminders(messages);
        // Process each message
        const prepared = [];
        for (let i = 0; i < messages.length; i++) {
            const msg = messages[i];
            const isLastMessage = i === messages.length - 1;
            const isLastWithReminders = msg === lastMessageWithReminders;
            // Decide whether to strip this message
            const shouldStrip = this.hasReminders(msg.content) &&
                msg.role === 'user' &&
                !isLastWithReminders;
            if (shouldStrip) {
                // Strip reminders from this message
                prepared.push(this.stripMessageReminders(msg));
            }
            else if (isLastMessage && msg.role === 'user' && freshContext) {
                // Add fresh context to the last user message
                prepared.push(this.addContextToMessage(msg, freshContext));
            }
            else {
                // Keep message as-is
                prepared.push({ ...msg });
            }
        }
        const slidePruned = this.pruneStaleSlideData(prepared);
        const excelPruned = this.pruneStaleExcelReadData(slidePruned);
        return this.stripExcelReadInfos(this.stripSlideInfos(excelPruned));
    }
    // ═══════════════════════════════════════════════════════════════════════
    // SLIDE-AWARE PRUNING
    // ═══════════════════════════════════════════════════════════════════════
    pruneStaleSlideData(messages) {
        const entriesBySlide = new Map();
        for (let i = 0; i < messages.length; i++) {
            const msg = messages[i];
            if (!Array.isArray(msg._slideInfos))
                continue;
            for (const entry of msg._slideInfos) {
                if (!entry || !entry.slideId)
                    continue;
                const list = entriesBySlide.get(entry.slideId) ?? [];
                list.push({ messageIndex: i, entry, role: msg.role });
                entriesBySlide.set(entry.slideId, list);
            }
        }
        if (entriesBySlide.size === 0) {
            return messages;
        }
        const detailRank = {
            layout: 0,
            complete: 1,
        };
        const staleByMessage = new Map();
        const keepByMessage = new Map();
        for (const [slideId, entries] of entriesBySlide) {
            if (entries.length === 0)
                continue;
            const lastWriteIdx = this.findLastIndex(entries, e => e.entry.type === 'write');
            const candidates = lastWriteIdx >= 0 ? entries.slice(lastWriteIdx) : entries;
            const winner = candidates.reduce((best, curr) => {
                // Policy: latest message wins. If timestamps tie, prefer richer detail.
                // This keeps "last re-read" behavior intuitive while still favoring
                // complete payloads when reads happen in the same instant.
                if (curr.entry.timestamp !== best.entry.timestamp) {
                    return curr.entry.timestamp > best.entry.timestamp ? curr : best;
                }
                const currRank = detailRank[curr.entry.detailLevel] ?? 0;
                const bestRank = detailRank[best.entry.detailLevel] ?? 0;
                if (currRank !== bestRank) {
                    return currRank > bestRank ? curr : best;
                }
                return curr.messageIndex >= best.messageIndex ? curr : best;
            }, candidates[0]);
            for (const item of entries) {
                const targetMap = item === winner ? keepByMessage : staleByMessage;
                const list = targetMap.get(item.messageIndex) ?? [];
                list.push(item.entry);
                targetMap.set(item.messageIndex, list);
            }
        }
        return messages.map((msg, idx) => {
            const staleEntries = staleByMessage.get(idx) ?? [];
            if (staleEntries.length === 0) {
                return msg;
            }
            const keepEntries = keepByMessage.get(idx) ?? [];
            const slideInfos = msg._slideInfos;
            const totalEntries = Array.isArray(slideInfos) ? slideInfos.length : 0;
            const allStale = staleEntries.length === totalEntries && totalEntries > 0;
            if (msg.role === 'tool') {
                return this.pruneToolMessage(msg, staleEntries, allStale);
            }
            if (msg.role === 'user') {
                if (allStale) {
                    return { ...msg, content: '' };
                }
                if (keepEntries.length > 0) {
                    return this.keepOnlyImages(msg);
                }
            }
            return msg;
        });
    }
    pruneToolMessage(message, staleEntries, allStale) {
        const slideInfos = message._slideInfos;
        const isSingleSlide = Array.isArray(slideInfos) && slideInfos.length === 1;
        const content = message.content;
        if (isSingleSlide && allStale && staleEntries[0]) {
            const stub = this.buildPrunedStub(staleEntries[0].slideId);
            return { ...message, content: JSON.stringify(stub) };
        }
        if (typeof content !== 'string') {
            return allStale ? { ...message, content: '' } : message;
        }
        try {
            const parsed = JSON.parse(content);
            if (parsed && Array.isArray(parsed.slides) && Array.isArray(parsed.contextIds)) {
                for (const entry of staleEntries) {
                    const idx = parsed.contextIds.indexOf(entry.slideId);
                    if (idx >= 0 && idx < parsed.slides.length) {
                        parsed.slides[idx] = this.buildPrunedSlideText(entry.slideId);
                    }
                }
                return { ...message, content: JSON.stringify(parsed) };
            }
        }
        catch (error) {
            console.warn('[MessagePreparer] Failed to parse tool content for pruning:', error);
        }
        if (allStale) {
            const stub = {
                _pruned: true,
                slideIds: staleEntries.map(e => e.slideId),
                note: 'These slide results were pruned. A newer version exists later in the conversation.',
            };
            return { ...message, content: JSON.stringify(stub) };
        }
        return message;
    }
    keepOnlyImages(message) {
        const content = message.content;
        if (Array.isArray(content)) {
            const images = content.filter(part => part.type === 'image_url');
            if (images.length === 0) {
                return { ...message, content: '' };
            }
            return { ...message, content: images };
        }
        return { ...message, content: '' };
    }
    buildPrunedSlideText(slideId) {
        return `[${slideId}: pruned — more recent version exists later]`;
    }
    buildPrunedStub(slideId) {
        return {
            _pruned: true,
            slideId,
            note: 'This slide was read/written here. A more recent version exists later in the conversation.',
        };
    }
    findLastIndex(arr, predicate) {
        for (let i = arr.length - 1; i >= 0; i--) {
            if (predicate(arr[i]))
                return i;
        }
        return -1;
    }
    stripSlideInfos(messages) {
        return messages.map(msg => {
            if (msg._slideInfos) {
                const copy = { ...msg };
                delete copy._slideInfos;
                return copy;
            }
            return msg;
        });
    }
    // ═══════════════════════════════════════════════════════════════════════
    // EXCEL RANGE-AWARE PRUNING
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Prune stale Excel read/write snapshots.
     *
     * Rules:
     * - Content and formatting are tracked independently (no cross-track pruning).
     * - New snapshot subsumes old only when:
     *   1) same sheet + same track
     *   2) new detailRank >= old detailRank
     *   3) new range fully contains old range
     *   4) new snapshot is at least as recent
     */
    pruneStaleExcelReadData(messages) {
        const entriesByGroup = new Map();
        for (let i = 0; i < messages.length; i++) {
            const msg = messages[i];
            if (!Array.isArray(msg._excelReadInfos))
                continue;
            for (const rawEntry of msg._excelReadInfos) {
                if (!rawEntry || !rawEntry.track || !rawEntry.sheetName || !rawEntry.rangeA1)
                    continue;
                const entry = {
                    ...rawEntry,
                    sheetName: this.normalizeExcelSheetName(rawEntry.sheetName),
                    rangeA1: this.normalizeExcelRange(rawEntry.rangeA1),
                };
                const key = `${entry.track}|${entry.sheetName}`;
                const list = entriesByGroup.get(key) ?? [];
                list.push({ messageIndex: i, entry, role: msg.role });
                entriesByGroup.set(key, list);
            }
        }
        if (entriesByGroup.size === 0) {
            return messages;
        }
        const staleByMessage = new Map();
        const markStale = (messageIndex, entry) => {
            const list = staleByMessage.get(messageIndex) ?? [];
            list.push(entry);
            staleByMessage.set(messageIndex, list);
        };
        for (const entries of entriesByGroup.values()) {
            if (entries.length <= 1)
                continue;
            for (const target of entries) {
                let bestSubsumingCandidate = null;
                for (const candidate of entries) {
                    if (candidate === target)
                        continue;
                    if (!this.isExcelEntrySubsuming(candidate.entry, target.entry))
                        continue;
                    const freshnessVsTarget = this.compareExcelEntryFreshness(candidate, target);
                    if (freshnessVsTarget <= 0)
                        continue;
                    if (!bestSubsumingCandidate) {
                        bestSubsumingCandidate = candidate;
                        continue;
                    }
                    if (this.compareExcelEntryFreshness(candidate, bestSubsumingCandidate) > 0) {
                        bestSubsumingCandidate = candidate;
                    }
                }
                if (bestSubsumingCandidate) {
                    markStale(target.messageIndex, target.entry);
                }
            }
        }
        if (staleByMessage.size === 0) {
            return messages;
        }
        return messages.map((msg, idx) => {
            const staleEntries = staleByMessage.get(idx) ?? [];
            if (staleEntries.length === 0) {
                return msg;
            }
            const excelInfos = msg._excelReadInfos;
            const totalEntries = Array.isArray(excelInfos) ? excelInfos.length : 0;
            const allStale = staleEntries.length >= totalEntries && totalEntries > 0;
            if (msg.role === 'tool') {
                return this.pruneExcelToolMessage(msg, staleEntries, allStale);
            }
            if (msg.role === 'user' && allStale) {
                return { ...msg, content: '' };
            }
            return msg;
        });
    }
    pruneExcelToolMessage(message, staleEntries, allStale) {
        if (!allStale) {
            // Keep mixed snapshots intact to avoid accidental partial JSON corruption.
            return message;
        }
        const grouped = staleEntries.reduce((acc, entry) => {
            const key = `${entry.track}:${entry.sheetName}`;
            const list = acc[key] ?? [];
            list.push(entry.rangeA1);
            acc[key] = list;
            return acc;
        }, {});
        return {
            ...message,
            content: JSON.stringify({
                _pruned: true,
                kind: 'excel',
                note: 'These Excel read/write snapshots were pruned. A newer/higher-detail superseding snapshot exists later in the conversation.',
                staleGroups: grouped,
            }),
        };
    }
    stripExcelReadInfos(messages) {
        return messages.map(msg => {
            if (msg._excelReadInfos) {
                const copy = { ...msg };
                delete copy._excelReadInfos;
                return copy;
            }
            return msg;
        });
    }
    compareExcelEntryFreshness(a, b) {
        const tsDiff = (a.entry.timestamp || 0) - (b.entry.timestamp || 0);
        if (tsDiff !== 0)
            return tsDiff;
        const idxDiff = a.messageIndex - b.messageIndex;
        if (idxDiff !== 0)
            return idxDiff;
        const rankDiff = (a.entry.detailRank || 0) - (b.entry.detailRank || 0);
        if (rankDiff !== 0)
            return rankDiff;
        return this.excelRangeCoverageScore(a.entry.rangeA1) - this.excelRangeCoverageScore(b.entry.rangeA1);
    }
    isExcelEntrySubsuming(candidate, target) {
        if (candidate.track !== target.track)
            return false;
        if (this.normalizeExcelSheetName(candidate.sheetName) !== this.normalizeExcelSheetName(target.sheetName)) {
            return false;
        }
        if ((candidate.detailRank || 0) < (target.detailRank || 0))
            return false;
        return this.excelRangeContains(candidate.rangeA1, target.rangeA1);
    }
    normalizeExcelSheetName(sheetName) {
        return String(sheetName || '').trim();
    }
    normalizeExcelRange(rangeA1) {
        const raw = String(rangeA1 || '').trim();
        if (!raw)
            return raw;
        if (raw.toUpperCase() === 'FULL_SHEET')
            return 'FULL_SHEET';
        if (raw.toUpperCase().startsWith('CHART:')) {
            const chartId = raw.slice(raw.indexOf(':') + 1).trim().toLowerCase();
            return `CHART:${chartId || 'unknown'}`;
        }
        const [_, rangeOnly] = raw.includes('!') ? raw.split('!', 2) : ['', raw];
        const compact = rangeOnly.replace(/\$/g, '').replace(/\s+/g, '');
        const upper = compact.toUpperCase();
        return upper || 'FULL_SHEET';
    }
    excelRangeContains(outerRangeA1, innerRangeA1) {
        const outer = this.normalizeExcelRange(outerRangeA1);
        const inner = this.normalizeExcelRange(innerRangeA1);
        const outerIsChart = this.isChartScopedRange(outer);
        const innerIsChart = this.isChartScopedRange(inner);
        if (outerIsChart || innerIsChart) {
            // Chart scopes only subsume same chart scopes, never sheet/range scopes.
            return outerIsChart && innerIsChart && outer === inner;
        }
        if (outer === 'FULL_SHEET')
            return true;
        if (inner === 'FULL_SHEET')
            return outer === 'FULL_SHEET';
        if (outer === inner)
            return true;
        const outerRect = this.parseExcelRangeRect(outer);
        const innerRect = this.parseExcelRangeRect(inner);
        if (!outerRect || !innerRect)
            return false;
        return (outerRect.startRow <= innerRect.startRow &&
            outerRect.endRow >= innerRect.endRow &&
            outerRect.startCol <= innerRect.startCol &&
            outerRect.endCol >= innerRect.endCol);
    }
    excelRangeCoverageScore(rangeA1) {
        const normalized = this.normalizeExcelRange(rangeA1);
        if (this.isChartScopedRange(normalized)) {
            return Number.MAX_SAFE_INTEGER - 1;
        }
        if (normalized === 'FULL_SHEET') {
            return Number.MAX_SAFE_INTEGER;
        }
        const rect = this.parseExcelRangeRect(normalized);
        if (!rect)
            return 0;
        return (rect.endRow - rect.startRow + 1) * (rect.endCol - rect.startCol + 1);
    }
    parseExcelRangeRect(rangeA1) {
        const normalized = this.normalizeExcelRange(rangeA1);
        if (!normalized || normalized === 'FULL_SHEET' || this.isChartScopedRange(normalized))
            return null;
        const [left, right = left] = normalized.split(':');
        const leftMatch = left.match(/^([A-Z]+)(\d+)$/);
        const rightMatch = right.match(/^([A-Z]+)(\d+)$/);
        if (!leftMatch || !rightMatch)
            return null;
        const leftCol = this.excelColLettersToNumber(leftMatch[1]);
        const rightCol = this.excelColLettersToNumber(rightMatch[1]);
        const leftRow = Number(leftMatch[2]);
        const rightRow = Number(rightMatch[2]);
        if (!Number.isFinite(leftCol) || !Number.isFinite(rightCol) || !Number.isFinite(leftRow) || !Number.isFinite(rightRow)) {
            return null;
        }
        return {
            startRow: Math.min(leftRow, rightRow),
            endRow: Math.max(leftRow, rightRow),
            startCol: Math.min(leftCol, rightCol),
            endCol: Math.max(leftCol, rightCol),
        };
    }
    isChartScopedRange(rangeA1) {
        return String(rangeA1 || '').trim().toUpperCase().startsWith('CHART:');
    }
    excelColLettersToNumber(colLetters) {
        let result = 0;
        const upper = colLetters.toUpperCase();
        for (let i = 0; i < upper.length; i++) {
            const code = upper.charCodeAt(i);
            if (code < 65 || code > 90)
                return 0;
            result = result * 26 + (code - 64);
        }
        return result;
    }
    /**
     * Find the last message in the array that contains reminders
     */
    findLastMessageWithReminders(messages) {
        for (let i = messages.length - 1; i >= 0; i--) {
            const msg = messages[i];
            if (msg.role === 'user' && this.hasReminders(msg.content)) {
                return msg;
            }
        }
        return null;
    }
    /**
     * Strip reminders from a message
     *
     * After stripping, any text parts that become empty are REMOVED
     * to avoid API errors from empty content parts.
     *
     * IMPORTANT: Also removes images associated with reminders.
     * If a reminder text part contains REMINDER_IMAGE_MARKER, the next
     * image_url part is also removed (it was part of the reminder).
     */
    stripMessageReminders(message) {
        const content = message.content;
        if (typeof content === 'string') {
            const stripped = this.stripReminders(content);
            console.log(`[MessagePreparer] 🧹 Stripped reminders from string content (${content.length} → ${stripped.length} chars)`);
            return {
                ...message,
                content: stripped,
            };
        }
        if (Array.isArray(content)) {
            const originalCount = content.length;
            const strippedContent = [];
            // Track indices of images that should be removed (associated with reminders)
            const imageIndicesToRemove = new Set();
            // First pass: identify which images are associated with reminders
            for (let i = 0; i < content.length; i++) {
                const part = content[i];
                if (part.type === 'text') {
                    // Check if this text part is a reminder with an associated image marker
                    const isReminder = part.text.includes(REMINDER_OPEN_TAG);
                    const hasImageMarker = part.text.includes(REMINDER_IMAGE_MARKER);
                    if (isReminder && hasImageMarker) {
                        // Find the next image_url part and mark it for removal
                        for (let j = i + 1; j < content.length; j++) {
                            if (content[j].type === 'image_url') {
                                imageIndicesToRemove.add(j);
                                console.log(`[MessagePreparer] 🖼️ Marking image at index ${j} for removal (associated with reminder at index ${i})`);
                                break;
                            }
                            // Stop if we hit another text part (image must be adjacent or near)
                            if (content[j].type === 'text') {
                                break;
                            }
                        }
                    }
                }
            }
            // Second pass: strip reminders from text and filter out marked images
            for (let i = 0; i < content.length; i++) {
                const part = content[i];
                // Skip images that are associated with reminders
                if (part.type === 'image_url' && imageIndicesToRemove.has(i)) {
                    console.log(`[MessagePreparer] 🗑️ Removing reminder-associated image at index ${i}`);
                    continue;
                }
                if (part.type === 'text') {
                    const stripped = this.stripReminders(part.text);
                    // Only keep non-empty text parts
                    if (stripped && stripped.trim().length > 0) {
                        strippedContent.push({
                            ...part,
                            text: stripped,
                        });
                    }
                    else {
                        console.log(`[MessagePreparer] 🗑️ Removing empty text part after stripping (index ${i})`);
                    }
                }
                else {
                    // Keep other parts (images not marked for removal)
                    strippedContent.push(part);
                }
            }
            const removedCount = originalCount - strippedContent.length;
            const removedImages = imageIndicesToRemove.size;
            console.log('[MessagePreparer] 🧹 Stripped reminders from array content:', {
                originalParts: originalCount,
                remainingParts: strippedContent.length,
                removedTextParts: removedCount - removedImages,
                removedImages: removedImages
            });
            // If all parts were removed, return empty string to avoid empty array
            if (strippedContent.length === 0) {
                console.log('[MessagePreparer] ⚠️ All content parts were reminders - returning empty string');
                return {
                    ...message,
                    content: '',
                };
            }
            // If only one text part remains, simplify to string for cleaner output
            if (strippedContent.length === 1 && strippedContent[0].type === 'text') {
                console.log('[MessagePreparer] 📝 Simplified to single string (was array with 1 text part)');
                return {
                    ...message,
                    content: strippedContent[0].text,
                };
            }
            return {
                ...message,
                content: strippedContent,
            };
        }
        return message;
    }
    /**
     * Add fresh context to a message
     */
    addContextToMessage(message, context) {
        const reminderContent = this.buildReminderContent(context);
        if (!reminderContent) {
            return message;
        }
        const content = message.content;
        if (typeof content === 'string') {
            // First strip any existing reminders, then add fresh ones
            const stripped = this.stripReminders(content);
            return {
                ...message,
                content: `${stripped}\n\n${reminderContent}`,
            };
        }
        if (Array.isArray(content)) {
            // Strip existing reminders from text parts, then append new reminders
            const strippedContent = content.map(part => {
                if (part.type === 'text') {
                    return {
                        ...part,
                        text: this.stripReminders(part.text),
                    };
                }
                return part;
            });
            // Append reminder as new text part
            strippedContent.push({
                type: 'text',
                text: reminderContent,
            });
            return {
                ...message,
                content: strippedContent,
            };
        }
        return message;
    }
    // ═══════════════════════════════════════════════════════════════════════
    // UTILITIES
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Convert content to text for analysis
     */
    contentToText(content) {
        if (typeof content === 'string') {
            return content;
        }
        if (Array.isArray(content)) {
            return content
                .filter((part) => part.type === 'text')
                .map(part => part.text)
                .join(' ');
        }
        return '';
    }
    /**
     * Estimate token count for a message (rough approximation)
     */
    estimateTokens(content) {
        const text = this.contentToText(content);
        // ~4 chars per token for English text
        let tokens = Math.ceil(text.length / 4);
        // Add overhead for images if content is array
        if (Array.isArray(content)) {
            const imageCount = content.filter(part => part.type === 'image_url').length;
            tokens += imageCount * 100; // ~100 tokens per image reference
        }
        return tokens + 10; // Add overhead
    }
    /**
     * Create a user message with reminders already added as content array
     *
     * Convenience method for creating a new message with context.
     * Returns content as ContentPart[] with each reminder as a separate part.
     */
    async createUserMessageWithContext(text, options = {}) {
        const collector = this.getReminderCollector('createUserMessageWithContext');
        if (!collector || typeof collector.collectContext !== 'function') {
            return {
                role: 'user',
                content: text,
            };
        }
        let context;
        try {
            context = await collector.collectContext(options);
        }
        catch (error) {
            console.warn('[MessagePreparer] Failed to collect reminder context in createUserMessageWithContext:', error);
            return {
                role: 'user',
                content: text,
            };
        }
        const contentWithReminders = this.addRemindersToMessage(text, context, 'createUserMessageWithContext');
        return {
            role: 'user',
            content: contentWithReminders,
        };
    }
}
// ═══════════════════════════════════════════════════════════════════════════
// SINGLETON
// ═══════════════════════════════════════════════════════════════════════════
let messagePreparerInstance = null;
/**
 * Get the MessagePreparer singleton
 */
export function getMessagePreparer() {
    if (!messagePreparerInstance) {
        messagePreparerInstance = new MessagePreparer();
    }
    return messagePreparerInstance;
}
